'use client'

import React from 'react'

/**
 * Error boundary specifically for markdown rendering.
 * If react-markdown or a remark plugin (e.g. remark-gfm) throws at render time
 * we fall back to a safe <pre> block so the chat widget never crashes.
 */
export class MarkdownErrorBoundary extends React.Component<
  { raw: string; children: React.ReactNode },
  { hasError: boolean; errorMessage?: string }
> {
  constructor(props: { raw: string; children: React.ReactNode }) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: any) {
    return { hasError: true, errorMessage: error?.message || String(error) }
  }

  componentDidCatch(error: any, info: any) {
    // Lightweight diagnostic – avoid spamming logs
    console.warn('[CHAT_MARKDOWN_FALLBACK] render_failed', {
      error: error?.message || String(error),
      componentStack: info?.componentStack,
    })
  }

  render() {
    if (this.state.hasError) {
      // Minimal manual formatting: convert bullet lines and inline code
      const raw = this.props.raw
      const lines = raw.split(/\r?\n/)
      const elements = lines.map((line, idx) => {
        const trimmed = line.trim()
        // Bullet list detection
        if (/^[-*]\s+/.test(trimmed)) {
          // Render as list item; group consecutive list items later
          return (
            <li key={idx} className="ml-4 list-disc">
              {trimmed.replace(/^[-*]\s+/, '')}
            </li>
          )
        }
        // Inline code spans wrapped in backticks
        const parts = [] as React.ReactNode[]
        const codeRegex = /`([^`]+)`/g
        let lastIndex = 0
        let match: RegExpExecArray | null
        while ((match = codeRegex.exec(line))) {
          if (match.index > lastIndex) {
            parts.push(line.slice(lastIndex, match.index))
          }
          parts.push(
            <code
              key={match.index}
              className="px-1 py-0.5 rounded bg-gray-200 text-[11px]"
            >
              {match[1]}
            </code>
          )
          lastIndex = match.index + match[0].length
        }
        if (lastIndex < line.length) {
          parts.push(line.slice(lastIndex))
        }
        return (
          <p key={idx} className="my-1 leading-relaxed whitespace-pre-wrap">
            {parts.length > 0 ? parts : line}
          </p>
        )
      })

      // Group list items into <ul>
      const grouped: React.ReactNode[] = []
      let buffer: React.ReactNode[] = []
      for (const el of elements) {
        if (React.isValidElement(el) && el.type === 'li') {
          buffer.push(el)
        } else {
          if (buffer.length > 0) {
            grouped.push(
              <ul key={`ul-${grouped.length}`} className="my-2 pl-1">
                {buffer}
              </ul>
            )
            buffer = []
          }
          grouped.push(el)
        }
      }
      if (buffer.length > 0) {
        grouped.push(
          <ul key={`ul-${grouped.length}`} className="my-2 pl-1">
            {buffer}
          </ul>
        )
      }

      return (
        <div className="chat-markdown prose prose-sm max-w-none">{grouped}</div>
      )
    }
    return this.props.children
  }
}

export default MarkdownErrorBoundary
